
const TOKEN_KEY = "ecogynize.auth.token";
const USER_KEY = "ecogynize.auth.user";
const LEGACY_TOKEN_KEY = "token";
const LEGACY_USER_KEY = "user";

/**
 * @typedef {Record<string, any> & {
 *   email?: string,
 *   name?: string,
 *   username?: string,
 *   organization_name?: string,
 *   org_info_completed?: boolean,
 *   org_info_skipped?: boolean
 * }} AuthUser
 */

/**
 * Seconds of slack when judging expiry. A token about to die mid-flight is already useless:
 * it passes the check here, then 401s on arrival, which is the exact bounce this guards
 * against. Treating the last few seconds as expired costs nothing.
 */
const EXPIRY_SKEW_SECONDS = 10;

/**
 * True when a JWT's `exp` claim is in the past.
 *
 * The access token lives 60 minutes (backend/app.py). Nothing here validated that, so an
 * hour after signing in `Boolean(token)` still reported a session: the router treated the
 * user as authenticated, rendered a protected page, and the first API call came back 401 —
 * clearing the session and redirecting to /Login. From the outside that looks like "the
 * site always sends me to login until I log in again".
 *
 * This reads the claim, it does not verify the signature — the server does that, and is
 * the only party that can. The point is not to trust the token, it is to stop presenting
 * one we can already see is dead.
 */
const isExpired = (token) => {
  if (!token) return false;
  const parts = String(token).split(".");
  if (parts.length !== 3) return false;   // not a JWT — let the server judge it
  try {
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    if (typeof payload?.exp !== "number") return false;  // no expiry claim to check
    return payload.exp <= Date.now() / 1000 + EXPIRY_SKEW_SECONDS;
  } catch {
    return false;   // unreadable payload is the server's call, not ours
  }
};

const safeParse = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const mirrorLegacySession = (token, user) => {
  if (token) sessionStorage.setItem(LEGACY_TOKEN_KEY, token);
  else sessionStorage.removeItem(LEGACY_TOKEN_KEY);

  if (user) sessionStorage.setItem(LEGACY_USER_KEY, JSON.stringify(user));
  else sessionStorage.removeItem(LEGACY_USER_KEY);
};

export const authStorage = {
  migrateLegacySession() {
    const currentToken = localStorage.getItem(TOKEN_KEY);
    const legacyToken = sessionStorage.getItem(LEGACY_TOKEN_KEY);
    const legacyUser = safeParse(sessionStorage.getItem(LEGACY_USER_KEY));

    if (!currentToken && legacyToken) {
      localStorage.setItem(TOKEN_KEY, legacyToken);
      if (legacyUser) localStorage.setItem(USER_KEY, JSON.stringify(legacyUser));
    }

    const token = localStorage.getItem(TOKEN_KEY);
    const user = safeParse(localStorage.getItem(USER_KEY));
    mirrorLegacySession(token, user);
  },

  getToken() {
    const token = localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(LEGACY_TOKEN_KEY);
    if (token && isExpired(token)) {
      // Drop it here rather than let a caller present it. Leaving it in storage means the
      // next read reports a session again, and the user gets bounced once per navigation.
      this.clearSession();
      return null;
    }
    return token;
  },

  /** Exposed for tests and for callers that need the check without the side effect. */
  isTokenExpired: isExpired,

  /** @returns {AuthUser | null} */
  getUser() {
    return safeParse(localStorage.getItem(USER_KEY)) || safeParse(sessionStorage.getItem(LEGACY_USER_KEY));
  },

  /** @param {{ token: string, user: AuthUser }} session */
  setSession({ token, user }) {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user || {}));
    mirrorLegacySession(token, user || {});
    window.dispatchEvent(new CustomEvent("ecogynize:auth-changed"));
  },

  /** @param {Partial<AuthUser>} patch */
  updateUser(patch) {
    const user = { ...(this.getUser() || {}), ...patch };
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    mirrorLegacySession(this.getToken(), user);
    window.dispatchEvent(new CustomEvent("ecogynize:auth-changed"));
    return user;
  },

  clearSession() {
    // A no-op when there is nothing to clear. Announcing "auth-changed" for a session that
    // did not exist is reporting a change that did not happen, and it closes a feedback
    // loop: every 401 cleared the session, every clear fired the event, and any listener
    // that refetches on it — useDevSession does — issued another request that 401'd again.
    // That ran as fast as the network allowed.
    const hadSomething = Boolean(
      localStorage.getItem(TOKEN_KEY) || localStorage.getItem(USER_KEY) ||
      sessionStorage.getItem(LEGACY_TOKEN_KEY) || sessionStorage.getItem(LEGACY_USER_KEY)
    );
    if (!hadSomething) return;

    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    mirrorLegacySession(null, null);
    window.dispatchEvent(new CustomEvent("ecogynize:auth-changed"));
  },
};

authStorage.migrateLegacySession();
